import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import './GoalsManager.css';


const TIPOS = [
  { value: 'economia',    label: '💰 Economia',     cor: '#10b981' },
  { value: 'investimento',label: '📈 Investimento', cor: '#3b82f6' },
  { value: 'pagamento',   label: '💳 Pagamento',    cor: '#f59e0b' },
  { value: 'reserva',     label: '🛡️ Reserva',      cor: '#8b5cf6' },
];

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const GoalsManager = ({ onClose }) => {
  const { authFetch } = useAuth();
  const [goals, setGoals]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]  = useState(null);
  const [toast, setToast]      = useState(null);
  const [form, setForm] = useState({
    titulo: '', descricao: '', valor_alvo: '', valor_atual: '',
    tipo: 'economia', prazo: '', cor: '#10b981', icone: '🎯',
  });

  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });
  const [confirmAporte, setConfirmAporte] = useState({ show: false, goal: null, value: '' });


  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const res  = await authFetch('/api/goals');
      const data = await res.json();
      setGoals(Array.isArray(data) ? data : []);
    } catch { showToast('Erro ao carregar metas', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchGoals(); }, []);

  const openAdd = () => {
    setForm({ titulo: '', descricao: '', valor_alvo: '', valor_atual: '', tipo: 'economia', prazo: '', cor: '#10b981', icone: '🎯' });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (g) => {
    setForm({
      titulo: g.titulo, descricao: g.descricao || '',
      valor_alvo: g.valor_alvo, valor_atual: g.valor_atual || 0,
      tipo: g.tipo, prazo: g.prazo || '', cor: g.cor || '#10b981', icone: g.icone || '🎯',
    });
    setEditing(g.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, valor_alvo: parseFloat(form.valor_alvo), valor_atual: parseFloat(form.valor_atual || 0) };
      if (editing) {
        await authFetch(`/api/goals/${editing}`, { method: 'PUT', body: JSON.stringify(body) });
        showToast('Meta atualizada!');
      } else {
        await authFetch('/api/goals', { method: 'POST', body: JSON.stringify(body) });
        showToast('Meta criada!');
      }
      setShowForm(false);
      fetchGoals();
    } catch (err) { 
      const errorMsg = err.response?.data?.message || err.message || 'Erro ao salvar meta';
      showToast(errorMsg, 'error'); 
    }
  };

  const handleDelete = async () => {
    const { id } = confirmDelete;
    if (!id) return;
    
    setLoading(true);
    try {
      await authFetch(`/api/goals/${id}`, { method: 'DELETE' });
      showToast('Meta removida!');
      setConfirmDelete({ show: false, id: null });
      fetchGoals();
    } catch { 
      showToast('Erro ao remover meta', 'error'); 
    } finally {
      setLoading(false);
    }
  };

  const handleAporte = async () => {
    const { goal, value } = confirmAporte;
    const valor = parseFloat(value || 0);
    if (!valor || valor <= 0) return;

    setLoading(true);
    const novoAtual = parseFloat(goal.valor_atual || 0) + valor;
    try {
      await authFetch(`/api/goals/${goal.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...goal, valor_atual: novoAtual }),
      });
      showToast(`Aporte de ${fmt(valor)} registrado!`);
      setConfirmAporte({ show: false, goal: null, value: '' });
      fetchGoals();
    } catch { 
      showToast('Erro ao registrar aporte', 'error'); 
    } finally {
      setLoading(false);
    }
  };


  const getProgress = (goal) => Math.min(100, ((goal.valor_atual || 0) / (goal.valor_alvo || 1)) * 100);

  return (
    <div className="goals-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="goals-modal">
        {toast && <div className={`goals-toast ${toast.type}`}>{toast.type === 'success' ? '✅' : '❌'} {toast.msg}</div>}

        <div className="goals-header">
          <div>
            <h2>🎯 Metas Financeiras</h2>
            <p>{goals.filter(g => g.status === 'ativa').length} metas ativas</p>
          </div>
          <div className="goals-header-actions">
            <button className="goals-btn-primary" onClick={openAdd}>+ Nova Meta</button>
            <button className="modal-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="goals-list">
          {loading ? (
            <div className="goals-empty">⏳ Carregando...</div>
          ) : goals.length === 0 ? (
            <div className="goals-empty">
              <span>🎯</span>
              <p>Nenhuma meta criada ainda</p>
              <button className="goals-btn-primary" onClick={openAdd}>Criar primeira meta</button>
            </div>
          ) : (
            goals.map(g => {
              const progress = getProgress(g);
              const tipo = TIPOS.find(t => t.value === g.tipo) || TIPOS[0];
              const isCompleta = progress >= 100;
              const diasRestantes = g.prazo ? Math.ceil((new Date(g.prazo) - new Date()) / (1000 * 60 * 60 * 24)) : null;
              return (
                <div key={g.id} className={`goal-card ${isCompleta ? 'completa' : ''}`}>
                  <div className="goal-card-top">
                    <div className="goal-icon" style={{ background: `${g.cor}22` }}>{g.icone}</div>
                    <div className="goal-info">
                      <h3>{g.titulo}</h3>
                      {g.descricao && <p>{g.descricao}</p>}
                      <span className="goal-tipo" style={{ color: g.cor, background: `${g.cor}18` }}>{tipo.label}</span>
                    </div>
                    <div className="goal-card-actions">
                      <button onClick={() => setConfirmAporte({ show: true, goal: g, value: '' })} title="Registrar aporte" className="goal-btn-aporte">💸</button>
                      <button onClick={() => openEdit(g)} title="Editar" className="goal-btn-edit">✏️</button>
                      <button onClick={() => setConfirmDelete({ show: true, id: g.id })} title="Excluir" className="goal-btn-delete">🗑️</button>
                    </div>

                  </div>

                  {/* Barra de progresso */}
                  <div className="goal-progress-wrap">
                    <div className="goal-progress-bar">
                      <div className="goal-progress-fill" style={{ width: `${progress}%`, background: g.cor }} />
                    </div>
                    <div className="goal-progress-labels">
                      <span>{fmt(g.valor_atual || 0)} de {fmt(g.valor_alvo)}</span>
                      <span className="goal-pct" style={{ color: isCompleta ? '#10b981' : g.cor }}>
                        {isCompleta ? '✅ Concluída!' : `${progress.toFixed(1)}%`}
                      </span>
                    </div>
                  </div>

                  {diasRestantes !== null && (
                    <div className={`goal-prazo ${diasRestantes < 0 ? 'vencida' : diasRestantes <= 30 ? 'urgente' : ''}`}>
                      📅 {diasRestantes < 0 ? `Vencida há ${Math.abs(diasRestantes)} dias` : `${diasRestantes} dias restantes`}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Formulário */}
        {showForm && (
          <div className="goals-form-overlay" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
            <div className="goals-form-modal">
              <div className="goals-form-header">
                <h3>{editing ? 'Editar Meta' : 'Nova Meta'}</h3>
                <button className="modal-close-btn" onClick={() => setShowForm(false)}>✕</button>
              </div>
              <form onSubmit={handleSave} className="goals-form">
                <div className="gf-row">
                  <div className="gf-field">
                    <label>Título *</label>
                    <input required placeholder="Ex: Reserva de emergência"
                      value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} />
                  </div>
                  <div className="gf-field" style={{ flex: '0 0 60px' }}>
                    <label>Ícone</label>
                    <input placeholder="🎯" maxLength={2}
                      value={form.icone} onChange={e => setForm(p => ({ ...p, icone: e.target.value }))} />
                  </div>
                </div>
                <div className="gf-field">
                  <label>Tipo de Meta</label>
                  <select value={form.tipo} onChange={e => { const t = TIPOS.find(x => x.value === e.target.value); setForm(p => ({ ...p, tipo: e.target.value, cor: t?.cor || p.cor })); }}>
                    {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="gf-row">
                  <div className="gf-field">
                    <label>Valor Alvo (R$) *</label>
                    <input required type="number" step="0.01" min="0" placeholder="0,00"
                      value={form.valor_alvo} onChange={e => setForm(p => ({ ...p, valor_alvo: e.target.value }))} />
                  </div>
                  <div className="gf-field">
                    <label>Valor Atual (R$)</label>
                    <input type="number" step="0.01" min="0" placeholder="0,00"
                      value={form.valor_atual} onChange={e => setForm(p => ({ ...p, valor_atual: e.target.value }))} />
                  </div>
                </div>
                <div className="gf-row">
                  <div className="gf-field">
                    <label>Prazo (opcional)</label>
                    <input type="date" value={form.prazo} onChange={e => setForm(p => ({ ...p, prazo: e.target.value }))} />
                  </div>
                  <div className="gf-field">
                    <label>Cor</label>
                    <input type="color" value={form.cor} onChange={e => setForm(p => ({ ...p, cor: e.target.value }))} />
                  </div>
                </div>
                <div className="gf-field">
                  <label>Descrição (opcional)</label>
                  <input placeholder="Ex: Para emergências médicas"
                    value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
                </div>
                <div className="gf-actions">
                  <button type="button" className="gf-btn-cancel" onClick={() => setShowForm(false)}>Cancelar</button>
                  <button type="submit" className="gf-btn-save">{editing ? 'Salvar' : 'Criar Meta'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Dialogs de Confirmação Modernos */}
        <ConfirmDialog 
          show={confirmDelete.show}
          title="Excluir Meta?"
          message="Esta ação não pode ser desfeita. Todo o progresso desta meta será removido."
          confirmText="Sim, excluir"
          cancelText="Não, manter"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete({ show: false, id: null })}
          type="danger"
          isLoading={loading}
        />

        <ConfirmDialog 
          show={confirmAporte.show}
          title="Registrar Aporte"
          message={`Quanto você deseja adicionar à meta "${confirmAporte.goal?.titulo}"?`}
          confirmText="Confirmar Aporte"
          cancelText="Agora não"
          onConfirm={handleAporte}
          onCancel={() => setConfirmAporte({ show: false, goal: null, value: '' })}
          type="aporte"
          isLoading={loading}
          inputValue={confirmAporte.value}
          setInputValue={(v) => setConfirmAporte(p => ({ ...p, value: v }))}
          inputPlaceholder="0,00"
        />
      </div>
    </div>

  );
};

export default GoalsManager;
